import Decimal from 'decimal.js';

/**
 * Xuất nhập khẩu (DEC-537, migration 0241, docs/phases/P10/xnk.md).
 * Hằng số và hàm tính dùng chung cho mọi nhánh XNK: lô hàng nhập / xuất, mốc, chứng từ, chi phí lô + giá vốn,
 * kế hoạch nhập dài hạn, tỷ giá, lưu cont, cước, đánh giá NCC. Tiền VND không số lẻ; giá / ĐVT 2 số lẻ (giống DEC-298).
 */

export const TRADE_DIRECTIONS = ['import', 'export'] as const;
export type TradeDirection = (typeof TRADE_DIRECTIONS)[number];
export const TRADE_DIRECTION_LABELS: Record<TradeDirection, string> = { import: 'Nhập khẩu', export: 'Xuất khẩu' };
/** Hậu tố số lô: 001-LHN-281026 (nhập), 001-LHX-281026 (xuất), đánh lại từ 001 mỗi ngày như các chứng từ khác. */
export const TRADE_SHIPMENT_CODE_SUFFIX: Record<TradeDirection, string> = { import: 'LHN', export: 'LHX' };
export const SUPPLIER_CLAIM_CODE_SUFFIX = 'KNN';

export const TRADE_SHIPMENT_STATUSES = [
  'planning',
  'contracted',
  'in_production',
  'booked',
  'in_transit',
  'arrived',
  'customs',
  'cleared',
  'delivered',
  'closed',
  'cancelled',
] as const;
export type TradeShipmentStatus = (typeof TRADE_SHIPMENT_STATUSES)[number];
export const TRADE_SHIPMENT_STATUS_LABELS: Record<TradeShipmentStatus, string> = {
  planning: 'Đang lên kế hoạch',
  contracted: 'Đã ký hợp đồng',
  in_production: 'NCC đang sản xuất',
  booked: 'Đã booking',
  in_transit: 'Đang trên đường',
  arrived: 'Đã đến cảng',
  customs: 'Đang làm hải quan',
  cleared: 'Đã thông quan',
  delivered: 'Đã về kho / đã giao',
  closed: 'Đã đóng lô',
  cancelled: 'Đã hủy',
};

export interface TradeMilestoneDef {
  code: string;
  label: string;
  /** Mốc bắt buộc ghi ngày thực tế trước khi đóng lô */
  required: boolean;
  /** Trạng thái lô khi mốc có ngày thực tế (mốc sau cùng có ngày quyết định trạng thái) */
  status: TradeShipmentStatus;
}

/** 12 mốc lô nhập (phương án anh Tiến duyệt 28/10/2026). Thứ tự = thứ tự thời gian. */
export const IMPORT_MILESTONES: readonly TradeMilestoneDef[] = [
  { code: 'contract_signed', label: 'Ký hợp đồng / PI', required: true, status: 'contracted' },
  { code: 'deposit_paid', label: 'Đặt cọc / mở L/C', required: false, status: 'contracted' },
  { code: 'goods_ready', label: 'Hàng sẵn sàng', required: false, status: 'in_production' },
  { code: 'booked', label: 'Booking', required: false, status: 'booked' },
  { code: 'departed', label: 'Tàu chạy (ETD)', required: true, status: 'in_transit' },
  { code: 'docs_received', label: 'Nhận chứng từ', required: false, status: 'in_transit' },
  { code: 'arrived', label: 'Tàu đến (ETA)', required: true, status: 'arrived' },
  { code: 'customs_opened', label: 'Mở tờ khai', required: false, status: 'customs' },
  { code: 'customs_cleared', label: 'Thông quan', required: true, status: 'cleared' },
  { code: 'delivered_wh', label: 'Về kho', required: true, status: 'delivered' },
  { code: 'asia_received', label: 'Nhập Asia', required: true, status: 'delivered' },
  { code: 'closed', label: 'Đóng lô', required: false, status: 'closed' },
];

/** Mốc lô xuất: từ hợp đồng bán tới khi thu đủ tiền và đủ hồ sơ VAT 0%. */
export const EXPORT_MILESTONES: readonly TradeMilestoneDef[] = [
  { code: 'contract_signed', label: 'Ký hợp đồng / PI', required: true, status: 'contracted' },
  { code: 'deposit_received', label: 'Nhận cọc / L/C', required: false, status: 'contracted' },
  { code: 'goods_ready', label: 'Hàng sẵn sàng', required: false, status: 'in_production' },
  { code: 'booked', label: 'Booking', required: false, status: 'booked' },
  { code: 'customs_opened', label: 'Mở tờ khai xuất', required: true, status: 'customs' },
  { code: 'customs_cleared', label: 'Thông quan xuất', required: true, status: 'cleared' },
  { code: 'departed', label: 'Tàu chạy (ETD)', required: true, status: 'in_transit' },
  { code: 'docs_sent', label: 'Gửi bộ chứng từ', required: false, status: 'in_transit' },
  { code: 'arrived', label: 'Hàng đến (ETA)', required: false, status: 'delivered' },
  { code: 'paid_in_full', label: 'Thu đủ tiền', required: true, status: 'delivered' },
  { code: 'closed', label: 'Đóng lô', required: false, status: 'closed' },
];

export const tradeMilestones = (d: TradeDirection) => (d === 'import' ? IMPORT_MILESTONES : EXPORT_MILESTONES);

/** Trạng thái lô suy từ mốc có ngày thực tế muộn nhất theo thứ tự mốc. Lô hủy giữ nguyên. */
export function shipmentStatusFromMilestones(direction: TradeDirection, doneCodes: Iterable<string>): TradeShipmentStatus {
  const done = new Set(doneCodes);
  let status: TradeShipmentStatus = 'planning';
  for (const m of tradeMilestones(direction)) if (done.has(m.code)) status = m.status;
  return status;
}

export const TRADE_DOC_KINDS = [
  'contract',
  'proforma',
  'commercial_invoice',
  'packing_list',
  'bill_of_lading',
  'co',
  'cq',
  'mill_test',
  'insurance',
  'customs_declaration',
  'inspection',
  'arrival_notice',
  'delivery_order',
  'payment_proof',
  'other',
] as const;
export type TradeDocKind = (typeof TRADE_DOC_KINDS)[number];
export const TRADE_DOC_KIND_LABELS: Record<TradeDocKind, string> = {
  contract: 'Hợp đồng',
  proforma: 'Proforma Invoice (PI)',
  commercial_invoice: 'Commercial Invoice',
  packing_list: 'Packing List',
  bill_of_lading: 'Vận đơn (B/L)',
  co: 'C/O – Chứng nhận xuất xứ',
  cq: 'CQ – Chứng nhận chất lượng',
  mill_test: 'Mill test certificate',
  insurance: 'Bảo hiểm hàng hóa',
  customs_declaration: 'Tờ khai hải quan',
  inspection: 'Kiểm tra chuyên ngành / giám định',
  arrival_notice: 'Giấy báo hàng đến',
  delivery_order: 'Lệnh giao hàng (D/O)',
  payment_proof: 'Chứng từ thanh toán qua ngân hàng',
  other: 'Khác',
};

/** Bộ hồ sơ tối thiểu: nhập để thông quan + tính giá vốn; xuất để thông quan + hưởng VAT 0% (hợp đồng, tờ khai, thanh toán qua NH). */
export const TRADE_REQUIRED_DOCS: Record<TradeDirection, readonly TradeDocKind[]> = {
  import: ['contract', 'commercial_invoice', 'packing_list', 'bill_of_lading', 'customs_declaration'],
  export: ['contract', 'commercial_invoice', 'packing_list', 'customs_declaration', 'payment_proof'],
};

/** Chứng từ còn thiếu (chưa có bản nào không bị trả lại). */
export function missingTradeDocs(direction: TradeDirection, docs: { kind: string; status: string }[], extra: TradeDocKind[] = []): TradeDocKind[] {
  const have = new Set(docs.filter((d) => d.status !== 'rejected').map((d) => d.kind));
  return [...TRADE_REQUIRED_DOCS[direction], ...extra].filter((k, i, a) => a.indexOf(k) === i && !have.has(k));
}

export const TRADE_COST_KINDS = [
  'ocean_freight',
  'insurance',
  'local_charge',
  'thc',
  'do_fee',
  'customs_service',
  'inspection',
  'storage',
  'demurrage',
  'inland_trucking',
  'import_duty',
  'remedy_duty',
  'import_vat',
  'export_duty',
  'bank_fee',
  'other',
] as const;
export type TradeCostKind = (typeof TRADE_COST_KINDS)[number];
export type TradeCostAllocation = 'value' | 'weight' | 'line' | 'none';

export const TRADE_COST_KIND_META: Record<TradeCostKind, { label: string; allocation: TradeCostAllocation; inLandedCost: boolean }> = {
  ocean_freight: { label: 'Cước biển / cước chính', allocation: 'weight', inLandedCost: true },
  insurance: { label: 'Bảo hiểm', allocation: 'value', inLandedCost: true },
  local_charge: { label: 'Local charge', allocation: 'weight', inLandedCost: true },
  thc: { label: 'THC', allocation: 'weight', inLandedCost: true },
  do_fee: { label: 'Phí D/O', allocation: 'weight', inLandedCost: true },
  customs_service: { label: 'Dịch vụ hải quan', allocation: 'value', inLandedCost: true },
  inspection: { label: 'Kiểm tra chuyên ngành / giám định', allocation: 'value', inLandedCost: true },
  storage: { label: 'Lưu kho bãi', allocation: 'weight', inLandedCost: true },
  demurrage: { label: 'Lưu cont (DEM / DET)', allocation: 'weight', inLandedCost: true },
  inland_trucking: { label: 'Xe về kho', allocation: 'weight', inLandedCost: true },
  import_duty: { label: 'Thuế nhập khẩu', allocation: 'line', inLandedCost: true },
  remedy_duty: { label: 'Thuế chống bán phá giá / tự vệ', allocation: 'line', inLandedCost: true },
  import_vat: { label: 'VAT nhập khẩu (khấu trừ)', allocation: 'none', inLandedCost: false },
  export_duty: { label: 'Thuế xuất khẩu', allocation: 'line', inLandedCost: true },
  bank_fee: { label: 'Phí ngân hàng', allocation: 'value', inLandedCost: true },
  other: { label: 'Khác', allocation: 'value', inLandedCost: true },
};

export const TRADE_CURRENCIES = ['USD', 'CNY', 'JPY', 'KRW', 'EUR', 'TWD', 'THB', 'SGD'] as const;
export const INCOTERMS = ['EXW', 'FCA', 'FAS', 'FOB', 'CFR', 'CIF', 'CPT', 'CIP', 'DAP', 'DPU', 'DDP'] as const;
export type Incoterm = (typeof INCOTERMS)[number];
export const TRADE_LANGS = ['vi', 'en', 'zh', 'ja', 'ko'] as const;
export type TradeLang = (typeof TRADE_LANGS)[number];
export const TRADE_LANG_LABELS: Record<TradeLang, string> = { vi: 'Tiếng Việt', en: 'English', zh: '中文', ja: '日本語', ko: '한국어' };

/** Tải trọng hàng thường xếp được / cont (kg). Ước tính, sửa theo hãng tàu từng lô (⚠ anh kiểm với forwarder). */
export const CONTAINER_PAYLOAD_KG: Record<string, number> = { '20': 25000, '40': 26500, '40HC': 26500, '45': 27000 };

// ───────────────────────── Đơn mua ngoại tệ ─────────────────────────

/** Đơn giá VND (2 số lẻ) từ đơn giá ngoại tệ × tỷ giá dự kiến. */
export function vndFromForeign(foreignAmount: number, rate: number, decimals = 2): number {
  return new Decimal(foreignAmount).mul(rate).toDecimalPlaces(decimals, Decimal.ROUND_HALF_UP).toNumber();
}

// ───────────────────────── Thuế nhập khẩu ─────────────────────────

export interface ImportTaxInput {
  /** Trị giá tính thuế CIF đã quy VND */
  cifVnd: number;
  /** % thuế NK áp dụng (MFN hoặc ưu đãi đặc biệt nếu có C/O hợp lệ) */
  dutyRate: number;
  /** % thuế chống bán phá giá / tự vệ / chống trợ cấp (cộng dồn), 0 nếu không có */
  remedyRate?: number;
  /** Thuế phòng vệ tuyệt đối, VND (vd đ/tấn × tấn) */
  remedyAmount?: number;
  /** % VAT nhập khẩu */
  vatRate: number;
}
export interface ImportTaxResult {
  duty: number;
  remedy: number;
  vatBase: number;
  vat: number;
  /** Phần thuế vào giá vốn = thuế NK + thuế phòng vệ (VAT NK được khấu trừ, không vào giá vốn) */
  inCost: number;
}

/** Thuế NK = CIF × thuế suất; phòng vệ = CIF × % (+ tuyệt đối); VAT NK = (CIF + NK + phòng vệ) × VAT. Làm tròn VND. */
export function importTaxes(i: ImportTaxInput): ImportTaxResult {
  const r = (d: Decimal) => d.toDecimalPlaces(0, Decimal.ROUND_HALF_UP).toNumber();
  const cif = new Decimal(i.cifVnd);
  const duty = r(cif.mul(i.dutyRate).div(100));
  const remedy = r(cif.mul(i.remedyRate ?? 0).div(100).plus(i.remedyAmount ?? 0));
  const vatBase = r(cif.plus(duty).plus(remedy));
  const vat = r(new Decimal(vatBase).mul(i.vatRate).div(100));
  return { duty, remedy, vatBase, vat, inCost: duty + remedy };
}

export interface RemedyMatchInput {
  hsCodes: string[];
  country: string | null;
  producer: string | null;
  validFrom: string;
  validTo: string | null;
}
/** Biện pháp phòng vệ có áp cho mã HS + nước + nhà sản xuất vào ngày `on` không. HS khớp theo tiền tố (biện pháp ghi 4–8 số). */
export function remedyApplies(r: RemedyMatchInput, hsCode: string, country: string | null, producer: string | null, on: string): boolean {
  if (on < r.validFrom || (r.validTo && on > r.validTo)) return false;
  if (!r.hsCodes.some((h) => hsCode.startsWith(h))) return false;
  if (r.country && r.country !== country) return false;
  if (r.producer && (!producer || !producer.toLowerCase().includes(r.producer.toLowerCase()))) return false;
  return true;
}

// ───────────────────────── Chi phí lô → giá vốn ─────────────────────────

export interface ShipmentLineForCost {
  id: number;
  quantity: number;
  /** Giá trị hàng VND (CIF hoặc FOB quy đổi) – dùng chia theo giá trị */
  valueVnd: number;
  /** Kg tịnh của dòng – dùng chia theo khối lượng; thiếu thì chia theo giá trị */
  netKg?: number | null;
}
export interface ShipmentCostForAlloc {
  amount: number;
  allocation: TradeCostAllocation;
  inLandedCost: boolean;
  lineId?: number | null;
}

/**
 * Chia chi phí lô xuống từng dòng, ra chi phí / 1 ĐVT (2 số lẻ).
 * value: theo giá trị; weight: theo kg (dòng nào thiếu kg thì cả khoản đó chia theo giá trị); line: gánh hết vào 1 dòng; none / không vào giá vốn: bỏ.
 */
export function allocateShipmentCosts(lines: ShipmentLineForCost[], costs: ShipmentCostForAlloc[]): Map<number, number> {
  const totals = new Map<number, Decimal>(lines.map((l) => [l.id, new Decimal(0)]));
  const valid = lines.filter((l) => l.quantity > 0);
  if (valid.length) {
    const sumValue = valid.reduce((s, l) => s.plus(Math.max(0, l.valueVnd)), new Decimal(0));
    const sumQty = valid.reduce((s, l) => s.plus(l.quantity), new Decimal(0));
    const allKg = valid.every((l) => (l.netKg ?? 0) > 0);
    const sumKg = allKg ? valid.reduce((s, l) => s.plus(l.netKg!), new Decimal(0)) : new Decimal(0);
    const shareBy = (l: ShipmentLineForCost, mode: 'value' | 'weight') => {
      if (mode === 'weight' && allKg) return new Decimal(l.netKg!).div(sumKg);
      return sumValue.gt(0) ? new Decimal(Math.max(0, l.valueVnd)).div(sumValue) : new Decimal(l.quantity).div(sumQty);
    };
    for (const c of costs) {
      if (!c.inLandedCost || c.allocation === 'none' || !(c.amount > 0)) continue;
      if (c.allocation === 'line') {
        if (c.lineId != null && totals.has(c.lineId)) totals.set(c.lineId, totals.get(c.lineId)!.plus(c.amount));
        continue;
      }
      for (const l of valid) totals.set(l.id, totals.get(l.id)!.plus(shareBy(l, c.allocation).mul(c.amount)));
    }
  }
  const out = new Map<number, number>();
  for (const l of lines)
    out.set(l.id, l.quantity > 0 ? totals.get(l.id)!.div(l.quantity).toDecimalPlaces(2, Decimal.ROUND_HALF_UP).toNumber() : 0);
  return out;
}

// ───────────────────────── Kế hoạch nhập khẩu dài hạn ─────────────────────────

export const IMPORT_PLAN_DEFAULTS = { leadTimeDays: 75, reviewDays: 30, serviceLevel: 0.95, historyDays: 180 } as const;

/** z của phân phối chuẩn cho mức phục vụ (xấp xỉ Acklam, sai số < 1e-4 trong khoảng 0,5–0,999). */
export function serviceLevelZ(p: number): number {
  if (!(p > 0 && p < 1)) throw new Error('Mức phục vụ phải trong (0, 1)');
  const a = [-39.69683028665376, 220.9460984245205, -275.9285104469687, 138.357751867269, -30.66479806614716, 2.506628277459239] as const;
  const b = [-54.47609879822406, 161.5858368580409, -155.6989798598866, 66.80131188771972, -13.28068155211918] as const;
  const c = [-0.007784894002430293, -0.3223964580411365, -2.400758277161838, -2.549732539343734, 4.374664141464968, 2.938163982698783] as const;
  const d = [0.007784695709041462, 0.3224671290700398, 2.445134137142996, 3.754408661907416] as const;
  const lo = 0.02425;
  if (p < lo) {
    const q = Math.sqrt(-2 * Math.log(p));
    return (((((c[0] * q + c[1]) * q + c[2]) * q + c[3]) * q + c[4]) * q + c[5]) / ((((d[0] * q + d[1]) * q + d[2]) * q + d[3]) * q + 1);
  }
  if (p > 1 - lo) return -serviceLevelZ(1 - p);
  const q = p - 0.5;
  const r = q * q;
  return ((((((a[0] * r + a[1]) * r + a[2]) * r + a[3]) * r + a[4]) * r + a[5]) * q) / (((((b[0] * r + b[1]) * r + b[2]) * r + b[3]) * r + b[4]) * r + 1);
}

export interface ImportPlanInput {
  /** Lượng xuất bán theo ngày trong kỳ lịch sử (đủ historyDays phần tử, ngày không bán = 0) */
  dailyDemand: number[];
  /** Tồn Asia − SO3 chưa xuất */
  available: number;
  /** Đơn mua chưa về + lô đang trên đường */
  incoming: number;
  /** Nhu cầu đã biết thêm (dự án, BOQ đã chốt) trong khoảng LT + chu kỳ */
  knownExtraDemand?: number;
  leadTimeDays?: number;
  reviewDays?: number;
  serviceLevel?: number;
  moq?: number | null;
  packQty?: number | null;
}
export interface ImportPlanResult {
  avgDaily: number;
  sigmaDaily: number;
  safetyStock: number;
  reorderPoint: number;
  position: number;
  /** Số ngày đủ bán với (tồn + đang về), null khi không bán */
  daysOfCover: number | null;
  /** Số ngày tới hạn đặt hàng (âm = trễ), null khi không bán */
  orderInDays: number | null;
  suggestedQty: number;
  urgency: 'order_now' | 'order_soon' | 'ok' | 'no_demand';
}

const round3 = (n: Decimal) => n.toDecimalPlaces(3, Decimal.ROUND_HALF_UP).toNumber();

/**
 * ROP = d × LT + SS; SS = z × σ × √LT; vị thế = có thể dùng + đang về.
 * SL gợi ý = d × (LT + chu kỳ) + SS + nhu cầu đã biết − vị thế, làm tròn lên theo bó / kiện rồi tối thiểu MOQ.
 * Hạn đặt = (vị thế − SS) / d − LT ngày. Khẩn: đặt ngay khi vị thế ≤ ROP hoặc hạn ≤ 7 ngày; sớm khi hạn ≤ 30 ngày.
 */
export function importPlan(i: ImportPlanInput): ImportPlanResult {
  const lt = i.leadTimeDays ?? IMPORT_PLAN_DEFAULTS.leadTimeDays;
  const review = i.reviewDays ?? IMPORT_PLAN_DEFAULTS.reviewDays;
  const z = serviceLevelZ(i.serviceLevel ?? IMPORT_PLAN_DEFAULTS.serviceLevel);
  const n = i.dailyDemand.length;
  const sum = i.dailyDemand.reduce((s, x) => s.plus(Math.max(0, x)), new Decimal(0));
  const avg = n ? sum.div(n) : new Decimal(0);
  const variance = n > 1 ? i.dailyDemand.reduce((s, x) => s.plus(new Decimal(Math.max(0, x)).minus(avg).pow(2)), new Decimal(0)).div(n - 1) : new Decimal(0);
  const sigma = variance.sqrt();
  const ss = sigma.mul(z).mul(Math.sqrt(lt));
  const rop = avg.mul(lt).plus(ss);
  const position = new Decimal(i.available).plus(i.incoming);
  const extra = new Decimal(i.knownExtraDemand ?? 0);
  const base = { avgDaily: round3(avg), sigmaDaily: round3(sigma), safetyStock: round3(ss), reorderPoint: round3(rop), position: round3(position) };
  if (avg.lte(0) && extra.lte(0)) {
    return { ...base, daysOfCover: null, orderInDays: null, suggestedQty: 0, urgency: 'no_demand' };
  }
  let need = avg.mul(lt + review).plus(ss).plus(extra).minus(position);
  if (need.lt(0)) need = new Decimal(0);
  if (need.gt(0) && i.packQty && i.packQty > 0) need = need.div(i.packQty).ceil().mul(i.packQty);
  if (need.gt(0) && i.moq && need.lt(i.moq)) need = new Decimal(i.moq);
  const daysOfCover = avg.gt(0) ? position.minus(extra).div(avg).toDecimalPlaces(0, Decimal.ROUND_FLOOR).toNumber() : null;
  const orderInDays = avg.gt(0) ? position.minus(extra).minus(ss).div(avg).minus(lt).toDecimalPlaces(0, Decimal.ROUND_FLOOR).toNumber() : 0;
  const urgency = position.minus(extra).lte(rop) || (orderInDays ?? 0) <= 7 ? 'order_now' : (orderInDays ?? 0) <= 30 ? 'order_soon' : 'ok';
  return { ...base, daysOfCover, orderInDays, suggestedQty: round3(need), urgency };
}

/** Một cont còn trống bao nhiêu kg với tổng kg đã gom (âm = quá tải). */
export function containerFill(totalKg: number, size: string): { payloadKg: number; fillPct: number; spareKg: number } {
  const payloadKg = CONTAINER_PAYLOAD_KG[size] ?? CONTAINER_PAYLOAD_KG['20']!;
  return {
    payloadKg,
    fillPct: new Decimal(totalKg).div(payloadKg).mul(100).toDecimalPlaces(1, Decimal.ROUND_HALF_UP).toNumber(),
    spareKg: payloadKg - totalKg,
  };
}

// ───────────────────────── Lưu cont ─────────────────────────

/** Ngày hết free time (ngày tàu đến + số ngày miễn phí − 1) và số ngày còn lại tới hết hạn tính từ `today` (âm = đã quá). */
export function freeTimeStatus(arrivedOn: string, freeDays: number, today: string): { lastFreeDay: string; daysLeft: number } {
  const day = 86_400_000;
  const a = Date.parse(`${arrivedOn}T00:00:00Z`);
  const last = new Date(a + (Math.max(freeDays, 1) - 1) * day).toISOString().slice(0, 10);
  const daysLeft = Math.round((Date.parse(`${last}T00:00:00Z`) - Date.parse(`${today}T00:00:00Z`)) / day);
  return { lastFreeDay: last, daysLeft };
}

// ───────────────────────── Tỷ giá ─────────────────────────

/** Lãi (+) / lỗ (−) tỷ giá của 1 khoản trả NCC: (tỷ giá dự kiến − tỷ giá thực trả) × ngoại tệ. Thu tiền khách thì ngược dấu. */
export function fxGainLoss(amountForeign: number, plannedRate: number, actualRate: number, direction: 'pay' | 'receive' = 'pay'): number {
  const diff = new Decimal(plannedRate).minus(actualRate).mul(amountForeign);
  return (direction === 'pay' ? diff : diff.neg()).toDecimalPlaces(0, Decimal.ROUND_HALF_UP).toNumber();
}

/** So tỷ giá hôm nay với bình quân N ngày: % chênh (âm = đang rẻ hơn bình quân, nên mua ngoại tệ / trả sớm). */
export function fxVsAverage(today: number, history: number[]): { average: number; diffPct: number } | null {
  const h = history.filter((x) => x > 0);
  if (!h.length || !(today > 0)) return null;
  const avg = h.reduce((s, x) => s.plus(x), new Decimal(0)).div(h.length);
  return {
    average: avg.toDecimalPlaces(2, Decimal.ROUND_HALF_UP).toNumber(),
    diffPct: new Decimal(today).minus(avg).div(avg).mul(100).toDecimalPlaces(2, Decimal.ROUND_HALF_UP).toNumber(),
  };
}

// ───────────────────────── Cước ─────────────────────────

export interface FreightOptionInput {
  freightForeign: number;
  rate: number;
  localChargesVnd: number;
  inlandVnd: number;
  /** Số kg hàng thực xếp */
  cargoKg: number;
  transitDays: number;
  /** Giá trị lô VND (tính chi phí vốn nằm trên đường) */
  cargoValueVnd: number;
  /** Lãi vay năm, % */
  annualInterestPct: number;
}
/** Chi phí về kho / tấn + chi phí vốn trên đường / tấn (VND). */
export function freightCostPerTon(o: FreightOptionInput): { logisticsPerTon: number; capitalPerTon: number; totalPerTon: number } {
  if (!(o.cargoKg > 0)) throw new Error('Khối lượng hàng phải > 0');
  const tons = new Decimal(o.cargoKg).div(1000);
  const logistics = new Decimal(o.freightForeign).mul(o.rate).plus(o.localChargesVnd).plus(o.inlandVnd);
  const capital = new Decimal(o.cargoValueVnd).mul(o.annualInterestPct).div(100).mul(o.transitDays).div(365);
  const r = (d: Decimal) => d.div(tons).toDecimalPlaces(0, Decimal.ROUND_HALF_UP).toNumber();
  return { logisticsPerTon: r(logistics), capitalPerTon: r(capital), totalPerTon: r(logistics.plus(capital)) };
}

// ───────────────────────── Đánh giá NCC mở rộng ─────────────────────────

export interface SupplierScoreV2Input {
  /** Tỷ lệ 0–1 */
  onTimeRate: number | null;
  fullRate: number | null;
  /** Điểm giá 0–1 (1 = rẻ nhất thị trường), như DEC-157 */
  priceScore: number | null;
  /** Tỷ lệ giá trị hàng không đạt 0–1 */
  defectValueRate: number | null;
  /** Số khiếu nại mở quá 30 ngày */
  staleClaims: number;
  /** Tỷ lệ lô đủ hồ sơ trước khi tàu đến 0–1 */
  docsOnTimeRate: number | null;
}
/**
 * Điểm NCC 100 = đúng hẹn 30 + giao đủ 20 + giá 20 + chất lượng 20 + chứng từ 10 (phương án 28/10/2026, thay trọng số DEC-157 khi PR đánh giá NCC merge).
 * Tiêu chí chưa có dữ liệu: chia lại theo các tiêu chí có dữ liệu. Mỗi khiếu nại mở quá 30 ngày trừ 2 điểm chất lượng.
 */
export function supplierScoreV2(i: SupplierScoreV2Input): { score: number | null; grade: 'A' | 'B' | 'C' | 'D' | null } {
  const clamp = (x: number) => Math.min(1, Math.max(0, x));
  const parts: [number, number | null][] = [
    [30, i.onTimeRate],
    [20, i.fullRate],
    [20, i.priceScore],
    [20, i.defectValueRate == null ? null : clamp(1 - i.defectValueRate - (i.staleClaims * 2) / 20)],
    [10, i.docsOnTimeRate],
  ];
  const have = parts.filter(([, v]) => v != null) as [number, number][];
  if (!have.length) return { score: null, grade: null };
  const w = have.reduce((s, [wt]) => s + wt, 0);
  const score = Math.round((have.reduce((s, [wt, v]) => s + wt * clamp(v), 0) / w) * 1000) / 10;
  const grade = score >= 85 ? 'A' : score >= 70 ? 'B' : score >= 55 ? 'C' : 'D';
  return { score, grade };
}
